'use client';

import React, { useState, useEffect } from 'react';
import { createClient, getAuthRedirectUrl } from '@/utils/supabase/client';
import { registerApi, loginApi, resetPasswordApi, requestMagicLinkApi, recordConsentApi } from '@/lib/api';
import { evaluatePasswordStrength } from '@/lib/auth-errors';
import PasswordChecklist from '@/components/PasswordChecklist';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
}

function getEmailProviderUrl(email: string): { name: string; url: string } | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  if (domain === 'gmail.com' || domain === 'googlemail.com') return { name: 'Gmail', url: 'https://mail.google.com' };
  if (domain === 'outlook.com' || domain === 'hotmail.com' || domain === 'live.com') return { name: 'Outlook', url: 'https://outlook.live.com' };
  if (domain === 'yahoo.com' || domain === 'ymail.com') return { name: 'Yahoo Mail', url: 'https://mail.yahoo.com' };
  if (domain === 'protonmail.com' || domain === 'proton.me' || domain === 'pm.me') return { name: 'Proton Mail', url: 'https://mail.proton.me' };
  if (domain === 'icloud.com' || domain === 'me.com' || domain === 'mac.com') return { name: 'iCloud Mail', url: 'https://www.icloud.com/mail' };
  if (domain === 'zoho.com') return { name: 'Zoho Mail', url: 'https://mail.zoho.com' };
  if (domain === 'rediffmail.com') return { name: 'Rediffmail', url: 'https://mail.rediff.com' };
  return null;
}

function calculateAge(birthDateString: string): number {
  if (!birthDateString) return 0;
  const parts = birthDateString.split('-').map(Number);
  if (parts.length < 3 || isNaN(parts[0])) return 0;
  const birthDate = new Date(parts[0], parts[1] - 1, parts[2]);
  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function getAgeLimitDateBoundaries() {
  const today = new Date();
  const maxDobDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  const maxDobStr = maxDobDate.toISOString().split('T')[0];

  const minDobDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
  const minDobStr = minDobDate.toISOString().split('T')[0];

  return { maxDobStr, minDobStr };
}

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'magic' | 'forgot' | 'verify-email'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [magicUserData, setMagicUserData] = useState<{ user: any; token: string } | null>(null);
  const [magicCountdown, setMagicCountdown] = useState<number | null>(null);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState('');

  const { maxDobStr, minDobStr } = getAgeLimitDateBoundaries();

  const resetState = (newMode: 'signin' | 'signup' | 'magic' | 'forgot') => {
    setMode(newMode);
    setErrorMsg('');
    setSuccessMsg('');
    setMagicUserData(null);
    setMagicCountdown(null);
    setVerifyEmail('');
    setResendDone(false);
    setResendError('');
    setShowPassword(false);
    setIsPasswordFocused(false);
    setDob('');
    setAgreeTerms(false);
  };

  const completeLogin = (user: any, token?: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_auth_user', JSON.stringify(user));
      if (token) localStorage.setItem('kheldrop_token', token);
      window.dispatchEvent(new CustomEvent('kheldrop_auth_change', { detail: user }));
      const isCompleted = localStorage.getItem('kheldrop_onboarding_completed') === 'true';
      if (!isCompleted) {
        setTimeout(() => {
          window.dispatchEvent(new Event('kheldrop_open_onboarding'));
        }, 120);
      }
    }
    onSuccess(user);
    onClose();
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (magicCountdown === null) return;
    if (magicCountdown <= 0) {
      if (magicUserData) {
        completeLogin(magicUserData.user, magicUserData.token);
      }
      return;
    }
    const timer = setTimeout(() => {
      setMagicCountdown(prev => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [magicCountdown, magicUserData]);

  useEffect(() => {
    if (isOpen && !email && typeof window !== 'undefined') {
      const verified =
        localStorage.getItem('kheldrop_verified_email') ||
        localStorage.getItem('kheldrop_last_signup_email') ||
        '';
      if (verified) {
        setEmail(verified);
      }
    }
  }, [isOpen, email]);

  if (!isOpen) return null;

  const supabase = createClient();

  const handleResendVerification = async () => {
    if (!verifyEmail || resending || resendCooldown > 0) return;
    setResending(true);
    setResendDone(false);
    setResendError('');
    try {
      const emailRedirectTo = getAuthRedirectUrl('/auth/callback');

      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: verifyEmail.trim().toLowerCase(),
        options: {
          emailRedirectTo,
        },
      });

      if (error) {
        const msg = error.message?.toLowerCase() || '';
        if (msg.includes('rate') || msg.includes('once every') || (error as any).status === 429) {
          setResendError('Supabase security cooldown: Please wait 60 seconds before requesting another email.');
          setResendCooldown(60);
        } else if (msg.includes('already confirmed') || msg.includes('already verified')) {
          setResendError('Your email address is already verified! You can sign in now.');
        } else {
          setResendError(error.message || 'Failed to resend confirmation email.');
        }
      } else {
        setResendDone(true);
        setResendCooldown(60);
      }
    } catch (err: any) {
      setResendError(err?.message || 'Could not connect to authentication service.');
    } finally {
      setResending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl('/auth/callback'),
        },
      });
      if (error) throw error;
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrorMsg('Please enter your email');
      return;
    }

    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      if (mode === 'magic') {
        const magicRes = await requestMagicLinkApi(email);
        setMagicUserData({ user: magicRes.user, token: magicRes.token });
        setSuccessMsg(`Magic login link generated for ${email}!`);
        setMagicCountdown(3);

        supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: getAuthRedirectUrl('/auth/callback'),
          },
        }).catch(() => {});

      } else if (mode === 'forgot') {
        if (!password || password.length < 6) {
          throw new Error('New password must be at least 6 characters');
        }

        const resetRes = await resetPasswordApi(email, password);
        if (resetRes?.user) {
          completeLogin(resetRes.user, resetRes.token);
          return;
        }
        throw new Error('Password reset failed. Please try again.');

      } else if (mode === 'signup') {
        if (!agreeTerms) {
          throw new Error('Statutory requirement: You must accept KhelDrop\'s Terms of Service and Privacy Notice to create an account.');
        }
        if (!dob) {
          throw new Error('Please enter your Date of Birth. All players must be at least 18 years old.');
        }
        const age = calculateAge(dob);
        if (age < 18) {
          throw new Error('Strict age limit: You must be at least 18 years old to join KhelDrop. Minors are not permitted.');
        }

        if (!password || password.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }
        const pwdStrength = evaluatePasswordStrength(password);
        if (pwdStrength.score < 3) {
          throw new Error('Please choose a stronger password matching at least 3 checklist requirements');
        }

        if (typeof window !== 'undefined') {
          localStorage.setItem('kheldrop_last_signup_email', email.trim().toLowerCase());
        }

        let supabaseHandled = false;
        try {
          const emailRedirectTo = getAuthRedirectUrl('/auth/callback');

          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: name || email.split('@')[0],
                dob,
                date_of_birth: dob,
                age,
                is_adult: true,
              },
              emailRedirectTo,
            },
          });

          if (!error && data.user) {
            supabaseHandled = true;
            recordConsentApi(data.user.id || email, 'ACCOUNT_REGISTRATION_AND_TERMS', '2026.1', 'signup_clickwrap').catch(() => {});
            if (data.session) {
              completeLogin(data.user, data.session.access_token);
              return;
            }
            setVerifyEmail(email.trim().toLowerCase());
            setMode('verify-email');
            return;
          }
        } catch {
        }

        if (!supabaseHandled) {
          const backendRes = await registerApi(email, password, name || email.split('@')[0], dob, age);
          if (backendRes?.user) {
            recordConsentApi(backendRes.user.id || email, 'ACCOUNT_REGISTRATION_AND_TERMS', '2026.1', 'signup_clickwrap').catch(() => {});
            completeLogin(backendRes.user, backendRes.token);
            return;
          }
        }
      } else {
        let supabaseSuccess = false;
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          if (!error && data.user) {
            supabaseSuccess = true;
            completeLogin(data.user, data.session?.access_token);
            return;
          }
        } catch {
        }

        if (!supabaseSuccess) {
          const backendRes = await loginApi(email, password);
          if (backendRes?.user) {
            completeLogin(backendRes.user, backendRes.token);
            return;
          }
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Authentication error');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'verify-email') {
    const provider = getEmailProviderUrl(verifyEmail);
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-content auth-modal" onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'rgba(124, 58, 237, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary, #7C3AED)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>

            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 8px', color: 'var(--text)' }}>
              Verify your email
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.5 }}>
              We sent a verification link to
            </p>
            <p style={{
              fontSize: '15px',
              fontWeight: 600,
              color: 'var(--primary, #7C3AED)',
              margin: '0 0 16px',
              wordBreak: 'break-all',
            }}>
              {verifyEmail}
            </p>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
              Click the link in the email to activate your account.
              Check your spam folder if you don't see it within a few minutes.
            </p>

            {provider && (
              <a
                href={provider.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'var(--primary, #7C3AED)',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  marginBottom: '12px',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Open {provider.name}
              </a>
            )}

            {resendDone && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 14px',
                borderRadius: '8px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                color: '#4ADE80',
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '12px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Verification email resent
              </div>
            )}

            {resendError && (
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#EF4444',
                fontSize: '12.5px',
                lineHeight: 1.4,
                marginBottom: '12px',
                textAlign: 'left',
              }}>
                {resendError}
              </div>
            )}

            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resending || resendCooldown > 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                background: 'var(--surface-elevated, #1D2522)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                fontWeight: 600,
                fontSize: '14px',
                cursor: resending || resendCooldown > 0 ? 'not-allowed' : 'pointer',
                opacity: resending || resendCooldown > 0 ? 0.6 : 1,
                marginBottom: '12px',
              }}
            >
              {resending ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Resending...
                </>
              ) : resendCooldown > 0 ? (
                `Resend available in ${resendCooldown}s`
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Resend verification email
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => resetState('signin')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary, #7C3AED)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 0',
              }}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content auth-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h2 className="modal-title" style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
              {mode === 'signin'
                ? 'Sign In to KhelDrop'
                : mode === 'signup'
                ? 'Join KhelDrop'
                : mode === 'magic'
                ? 'Magic Link Login'
                : 'Reset Password'}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '4px', margin: '4px 0 0' }}>
              {mode === 'forgot'
                ? 'Enter your email and new password to instantly update your credentials.'
                : 'Find games, claim slots, and protect your reliability score.'}
            </p>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer', padding: '4px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {errorMsg && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid var(--danger, #EF4444)',
            color: '#F87171',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            marginBottom: '16px',
          }}>
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div style={{
            background: 'rgba(124, 58, 237, 0.12)',
            border: '1px solid var(--primary, #7C3AED)',
            color: '#C4B5FD',
            padding: '12px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            marginBottom: '16px',
          }}>
            <p style={{ margin: 0, fontWeight: 600 }}>{successMsg}</p>
            {magicUserData && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 8px' }}>
                  Supabase free-tier SMTP may delay external emails. Click below for instant sign in:
                </p>
                <button
                  type="button"
                  onClick={() => completeLogin(magicUserData.user, magicUserData.token)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'var(--primary)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '13px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  Instant 1-Click Sign In {magicCountdown !== null && magicCountdown > 0 ? `(${magicCountdown}s)` : ''}
                </button>
              </div>
            )}
          </div>
        )}
        {(mode === 'signin' || mode === 'signup') && (
          <>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '12px',
                borderRadius: '8px',
                background: 'var(--surface-elevated, #1D2522)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '16px',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z"
                />
                <path
                  fill="#34A853"
                  d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                />
                <path
                  fill="#EA4335"
                  d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                />
              </svg>
              Continue with Google
            </button>

            <p style={{
              margin: '-6px 0 14px',
              fontSize: '11px',
              color: 'var(--text-muted)',
              textAlign: 'center',
              lineHeight: 1.45,
            }}>
              By continuing with Google, you affirm you are <strong>18+</strong> and accept KhelDrop's{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Terms</a>,{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Privacy</a> &{' '}
              <a href="/age-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Age Policy (18+)</a>.
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '16px 0',
              color: 'var(--text-muted)',
              fontSize: '11px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              <span>OR</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleEmailAuth} method="post" action="#" autoComplete="on">
          {mode === 'signup' && (
            <>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label className="form-label" style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Full Name</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. Alex Sharma"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'var(--elevated)', border: '1px solid var(--border)', color: 'var(--text)' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label className="form-label" style={{ fontSize: '13px', margin: 0 }}>
                    Date of Birth <span style={{ color: 'var(--danger, #EF4444)' }}>*</span>
                  </label>
                  {dob && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '999px',
                      background: calculateAge(dob) >= 18 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: calculateAge(dob) >= 18 ? '#10B981' : '#F87171',
                      border: calculateAge(dob) >= 18 ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                    }}>
                      {calculateAge(dob) >= 18 ? `Age: ${calculateAge(dob)} (Eligible 18+)` : `Age: ${calculateAge(dob)} (Under 18 - Blocked)`}
                    </span>
                  )}
                </div>
                <input
                  className="form-input"
                  type="date"
                  value={dob}
                  onChange={e => {
                    setDob(e.target.value);
                    if (e.target.value) {
                      const calculatedAge = calculateAge(e.target.value);
                      if (calculatedAge < 18) {
                        setErrorMsg('Strict Age Limit: You must be at least 18 years old to join KhelDrop. Minors are not permitted.');
                      } else {
                        setErrorMsg('');
                      }
                    }
                  }}
                  max={maxDobStr}
                  min={minDobStr}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: 'var(--elevated)',
                    border: dob && calculateAge(dob) < 18 ? '1.5px solid var(--danger, #EF4444)' : '1px solid var(--border)',
                    color: 'var(--text)',
                    colorScheme: 'dark',
                  }}
                />
                <p style={{ margin: '5px 0 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  <span><strong>Strict 18+ policy:</strong> You must be 18 or older to register, reserve turf lobbies, or participate in games.</span>
                </p>
              </div>
            </>
          )}

          <div className="form-group" style={{ marginBottom: '14px' }}>
            <label className="form-label" style={{ display: 'block', fontSize: '13px', marginBottom: '6px' }}>Email Address</label>
            <input
              name="username"
              className="form-input"
              type="email"
              placeholder="you@domain.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username webauthn"
              required
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: 'var(--elevated)', border: '1px solid var(--border)', color: 'var(--text)' }}
            />
          </div>

          {mode !== 'magic' && (
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="form-label" style={{ fontSize: '13px', margin: 0 }}>
                  {mode === 'forgot' ? 'New Password' : 'Password'}
                </label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => resetState('forgot')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: 0,
                      fontWeight: 500,
                    }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  name="password"
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password webauthn'}
                  placeholder={
                    mode === 'signup'
                      ? 'Create a strong password (min 8 chars)'
                      : mode === 'forgot'
                      ? 'Enter new password (min 8 chars)'
                      : '••••••••'
                  }
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 44px 10px 14px',
                    borderRadius: '8px',
                    background: 'var(--elevated)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>

              {(mode === 'signup' || mode === 'forgot') && (
                <PasswordChecklist
                  password={password}
                  alwaysShow={true}
                />
              )}
            </div>
          )}

          {mode === 'signup' && (
              <div style={{
                marginBottom: '16px',
                marginTop: '12px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: agreeTerms ? 'rgba(16, 185, 129, 0.06)' : 'rgba(255, 255, 255, 0.03)',
                border: agreeTerms ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid var(--border)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                transition: 'all 0.2s ease',
              }}>
                <input
                  type="checkbox"
                  id="auth-agree-terms"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                  style={{ marginTop: '2px', cursor: 'pointer', accentColor: 'var(--primary)' }}
                  required
                />
                <label htmlFor="auth-agree-terms" style={{ fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.45, cursor: 'pointer', margin: 0 }}>
                  I solemnly affirm that I am <strong>18 years of age or older</strong> (competent to contract under Indian Contract Act, 1872) and agree to KhelDrop's{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline' }}>Terms</a>,{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline' }}>Privacy</a>, and{' '}
                  <a href="/age-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline' }}>Age Limitation Policy (DPDP §9)</a>.
                </label>
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || (mode === 'signup' && (!agreeTerms || !dob || calculateAge(dob) < 18))}
              style={{
                width: '100%',
                marginTop: '4px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 700,
                background: (mode === 'signup' && (!agreeTerms || !dob || calculateAge(dob) < 18)) ? '#374151' : 'var(--primary)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: (loading || (mode === 'signup' && (!agreeTerms || !dob || calculateAge(dob) < 18))) ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
            {loading
              ? 'Processing...'
              : mode === 'signin'
              ? 'Sign In'
              : mode === 'signup'
              ? 'Create Account & Start Playing'
              : mode === 'forgot'
              ? 'Reset Password & Sign In'
              : 'Send Magic Link / Instant Login'}
          </button>
        </form>

        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          {mode === 'signin' ? (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => resetState('signup')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
              >
                Sign Up
              </button>
              •
              <button
                type="button"
                onClick={() => resetState('magic')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                Magic Link
              </button>
            </>
          ) : mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => resetState('signin')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
              >
                Sign In
              </button>
              •
              <button
                type="button"
                onClick={() => resetState('magic')}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                Magic Link
              </button>
            </>
          ) : mode === 'forgot' ? (
            <>
              Remember your password?{' '}
              <button
                type="button"
                onClick={() => resetState('signin')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
              >
                Back to Sign In
              </button>
            </>
          ) : (
            <>
              Remember password?{' '}
              <button
                type="button"
                onClick={() => resetState('signin')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
              >
                Back to Sign In
              </button>
              •
              <button
                type="button"
                onClick={() => resetState('signup')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
              >
                Create Account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
